import { env } from '@/lib/env';

export interface ApiEnvelope<T> {
  success: boolean;
  data: T | null;
  message: string | null;
  code?: string | null;
  meta?: { page: number; limit: number; total: number };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type GetToken = () => Promise<string | null>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = env.outreachApiUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}/api/v1${suffix}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(
  getToken: GetToken,
  path: string,
  options: RequestOptions = {},
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...extraHeaders,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  let bodyString: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    bodyString = JSON.stringify(options.body);
  }

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: bodyString,
    signal: options.signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const json: ApiEnvelope<T> | null = text ? (JSON.parse(text) as ApiEnvelope<T>) : null;

  if (!response.ok || !json?.success) {
    const message = json?.message ?? response.statusText ?? 'Request failed';
    const code = json?.code ?? null;
    throw new ApiError(response.status, code, message);
  }

  return json.data as T;
}

/**
 * Variant of {@link request} that returns the data AND the `meta` block so
 * paginated callers can show "page N of M" without a second fetch. Returns
 * `meta = { page: 1, limit: data.length, total: data.length }` when the
 * backend didn't include one (so callers can render a single-page UI
 * uniformly).
 */
export async function requestWithMeta<T>(
  getToken: GetToken,
  path: string,
  options: RequestOptions = {},
  extraHeaders?: Record<string, string>,
): Promise<{ data: T; meta: { page: number; limit: number; total: number } }> {
  const token = await getToken();
  const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders };
  if (token) headers.Authorization = `Bearer ${token}`;
  let bodyString: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    bodyString = JSON.stringify(options.body);
  }
  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: bodyString,
    signal: options.signal,
  });
  const text = await response.text();
  const json: ApiEnvelope<T> | null = text ? (JSON.parse(text) as ApiEnvelope<T>) : null;
  if (!response.ok || !json?.success) {
    const message = json?.message ?? response.statusText ?? 'Request failed';
    const code = json?.code ?? null;
    throw new ApiError(response.status, code, message);
  }
  const data = json.data as T;
  const meta = json.meta ?? {
    page: 1,
    limit: Array.isArray(data) ? data.length : 1,
    total: Array.isArray(data) ? data.length : 1,
  };
  return { data, meta };
}

/** Returns an object whose methods carry the bearer token automatically. */
export function createApi(getToken: GetToken, extraHeaders?: Record<string, string>) {
  return {
    get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
      request<T>(getToken, path, { method: 'GET', query, signal }, extraHeaders),
    /** Paginated GET — returns { data, meta } instead of just data. */
    getPaged: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
      requestWithMeta<T>(getToken, path, { method: 'GET', query, signal }, extraHeaders),
    post: <T>(path: string, body?: unknown) =>
      request<T>(getToken, path, { method: 'POST', body }, extraHeaders),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(getToken, path, { method: 'PATCH', body }, extraHeaders),
    put: <T>(path: string, body?: unknown) =>
      request<T>(getToken, path, { method: 'PUT', body }, extraHeaders),
    del: <T>(path: string, body?: unknown) =>
      request<T>(getToken, path, { method: 'DELETE', body }, extraHeaders),
    upload: async <T>(path: string, file: File) => {
      const token = await getToken();
      const form = new FormData();
      form.append('file', file);
      const headers: Record<string, string> = { ...extraHeaders };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(buildUrl(path), {
        method: 'POST',
        headers,
        body: form,
      });
      const text = await response.text();
      const json: ApiEnvelope<T> | null = text ? (JSON.parse(text) as ApiEnvelope<T>) : null;
      if (!response.ok || !json?.success) {
        throw new ApiError(
          response.status,
          json?.code ?? null,
          json?.message ?? response.statusText,
        );
      }
      return json.data as T;
    },
    /**
     * GET for non-JSON payloads (CSV, PDF, etc). Returns the raw Blob plus
     * the filename parsed from `Content-Disposition` when present, so the
     * caller can trigger a browser download without hard-coding a name.
     * Error bodies are decoded as JSON envelopes when the server returned
     * one, matching the shape callers already handle on failed writes.
     */
    download: async (
      path: string,
      query?: RequestOptions['query'],
    ): Promise<{ blob: Blob; filename: string | null; contentType: string | null }> => {
      const token = await getToken();
      const headers: Record<string, string> = { ...extraHeaders };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(buildUrl(path, query), { method: 'GET', headers });
      if (!response.ok) {
        const text = await response.text();
        let message = response.statusText || 'Download failed';
        let code: string | null = null;
        try {
          const json = text ? (JSON.parse(text) as ApiEnvelope<unknown>) : null;
          if (json?.message) message = json.message;
          if (json?.code) code = json.code;
        } catch {
          // Non-JSON error body — surface the text as-is if present.
          if (text) message = text.slice(0, 500);
        }
        throw new ApiError(response.status, code, message);
      }
      const disposition = response.headers.get('content-disposition');
      const match = disposition?.match(/filename="?([^";]+)"?/i);
      return {
        blob: await response.blob(),
        filename: match?.[1] ?? null,
        contentType: response.headers.get('content-type'),
      };
    },
    /**
     * POST with FormData — useful when an endpoint accepts both form fields
     * and an optional file in the same request (e.g. /portal/invites with an
     * inline screenshot).
     */
    postForm: async <T>(path: string, form: FormData): Promise<T> => {
      const token = await getToken();
      const headers: Record<string, string> = { ...extraHeaders };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(buildUrl(path), {
        method: 'POST',
        headers,
        body: form,
      });
      const text = await response.text();
      const json: ApiEnvelope<T> | null = text ? (JSON.parse(text) as ApiEnvelope<T>) : null;
      if (!response.ok || !json?.success) {
        throw new ApiError(
          response.status,
          json?.code ?? null,
          json?.message ?? response.statusText,
        );
      }
      return json.data as T;
    },
  };
}

export type Api = ReturnType<typeof createApi>;
